import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import {
  CheckCircleFilled,
  CloseOutlined,
  DollarOutlined,
  MailOutlined,
  LinkOutlined,
  SendOutlined,
  SafetyCertificateOutlined,
  WhatsAppOutlined,
  InboxOutlined,
  DeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { CheckBox } from '../../../../../core/components';
import {
  Upload,
  Input,
  Select,
  message,
  Spin,
  Skeleton,
  Button as AntButton,
} from 'antd';
import type { UploadProps } from 'antd';

import Swal from 'sweetalert2';
import { JobApplication } from '../../../../../core/models/jobs/job-application.model';
import { ICheckboxOption } from '../../../../../core/interfaces';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { JobService } from '../../services/job.service';
import { validateCandidateCountry } from '../../../../../core/utils/country-validation';
interface Props {
  isOpen: boolean;
  job: any;
  onClose: () => void;
}

interface Option {
  value: string;
  label: React.ReactNode;
  dial_code: string;
}

const JobApplicationModal: React.FC<Props> = ({ isOpen, job, onClose }) => {
  const countryOptions: Option[] =
    require('../../../../../assets/countries.json').map((country: any) => ({
      value: country.code,
      dial_code: country.dial_code,
      label: (
        <div className="flex items-center w-12 lg:w-24">
          <img
            src={country.flag}
            alt={country.name}
            className="mr-2 lg:mr-2.5"
            style={{ width: '20px', height: '15px' }}
          />
          <span className="hidden lg:inline">({country.dial_code})</span>
        </div>
      ),
    }));

  const defaultCountry: Option = countryOptions.find(
    (option: any) => option.value === 'CR'
  )!;

  const [selectedCountry, setSelectedCountry] = useState<Option | null>(
    defaultCountry
  );

  const { Dragger } = Upload;

  type SelectValue = { label: string; value: string };

  const [selectedCurrency, setSelectedCurrency] = useState<SelectValue>({
    label: '$',
    value: 'US_DOLLAR',
  });

  const [application, setApplication] = useState<JobApplication>(
    new JobApplication()
  );

  const [inputErrors, setInputErrors] = useState<{
    [key: string]: string | null;
  }>({});

  const [spinner, setSpinner] = useState<boolean>(false);

  const [selecteds, setSelecteds] = useState<ICheckboxOption[]>([]);

  const [file, setFile] = useState<File | null>(null);

  const emailNotifiable = {
    name: 'ckbEmail',
    label: 'Email',
    value: 'ckbEmail',
    icon: <MailOutlined />,
    disabled: true,
  };
  const whatsappNotifiable = {
    name: 'ckbWhatsApp',
    label: 'WhatsApp',
    value: 'ckbWhatsApp',
    icon: <WhatsAppOutlined className="community-brand-icon--whatsapp" />,
    disabled: false,
  };

  const options = [emailNotifiable, whatsappNotifiable];
  const [countryError, setCountryError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const ExtractPersonalInfo = async (file: File) => {
    try {
      let response = await JobService.PersonalInfoExtraction(file);
      setSpinner(false);

      if (response) {
        const match = response.country_code
          ? countryOptions.find(
              (option) => option.dial_code === response.country_code
            )
          : countryOptions.find((option) => option.dial_code === '506');

        setSelectedCountry(match ?? null);

        setApplication((prevApplication) => ({
          ...prevApplication,
          firstname: response.first_name || '',
          lastname: response.last_name || '',
          email: response.email || '',
          phoneNumber: response.phone_number || '',
          websiteURL: response.linkedin_url || response.profile_url || '',
        }));
        setInputErrors({});

        if (response.country) {
          const error = validateCandidateCountry(
            job.countries,
            response.country,
            response.country_code
          );
          setCountryError(error);
        }
      }
    } catch (error: any) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: error.message,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
    } finally {
      setSpinner(false);
    }
  };

  const handleFileChange = (info: any) => {
    if (info.file && info.file.status && info.file.status === 'removed') {
      clearExtractedData();
    } else if (info.file) {
      setFile(info.file);
      ExtractPersonalInfo(info.file);
      setInputErrors((prev) => ({
        ...prev,
        file: null,
      }));
    }
  };

  const props: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.docx',
    beforeUpload(file) {
      setSpinner(true);
      const isPDF = file.type === 'application/pdf';
      const isDOCX =
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      if (!isPDF && !isDOCX) {
        setSpinner(false);
        message.error('Only PDF or DOCX files are allowed.');
        return Upload.LIST_IGNORE;
      }

      return false;
    },
    onChange: handleFileChange,
    onRemove() {
      clearExtractedData();
    },
  };

  useEffect(() => {
    setApplication(new JobApplication());
    setFile(null);
    setSelecteds([emailNotifiable]);
    setInputErrors({});
    setSelectedCountry(defaultCountry);
    setCountryError(null);
    setIsSubmitting(false);
    setIsSubmitted(false);
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      const currencyCode = job.currency ?? job.client?.currency;

      if (currencyCode) {
        const label = currencyCode === 'US_DOLLAR' ? '$' : '₡';
        setSelectedCurrency({ label, value: currencyCode });

        setApplication((prev) => ({
          ...prev,
          currency: currencyCode,
        }));
      } else {
        setApplication((prev) => ({
          ...prev,
          currency: selectedCurrency.value,
        }));
      }
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleCountryChange = (selectedOption: string | null) => {
    if (selectedOption) {
      setSelectedCountry(
        countryOptions.find((option: any) => option.value === selectedOption)!
      );
    }
  };

  const OnChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setApplication((prevApplication) => ({
      ...prevApplication,
      [name]: value,
    }));
    setInputErrors((prev) => ({
      ...prev,
      [name]: null,
    }));
  };

  const OnChangeNumberInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const numValue = parseInt(value);
    setApplication((prevApplication) => ({
      ...prevApplication,
      [name]: numValue,
    }));
    setInputErrors((prevErrors) => ({
      ...prevErrors,
      [name]: null,
    }));
  };

  const OnChangeSelected = (
    e: CheckboxChangeEvent,
    option: ICheckboxOption
  ) => {
    setSelecteds((prevSelecteds) => {
      if (e.target.checked) {
        return [...prevSelecteds, option];
      }
      return prevSelecteds.filter((item) => item.value !== option.value);
    });
  };

  function isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return regex.test(email);
  }

  function isValidURL(url: string): boolean {
    if (url === '') return true;
    const urlRegex = /^(https?:\/\/)([\w-]+\.)+([a-zA-Z]{2,24})(\/.*)?$/;
    return urlRegex.test(url);
  }

  const Save = async () => {
    try {
      setIsSubmitting(true);
      let response = await JobService.EasyApply(
        job.id,
        {
          ...application,
          phoneNumber: selectedCountry?.dial_code + application.phoneNumber,
          whatsappNotifiable: selecteds.some((x) => x.value === 'ckbWhatsApp'),
          emailNotifiable: selecteds.some((x) => x.value === 'ckbEmail'),
        },
        file!
      );
      if (response) {
        setIsSubmitted(true);
      }
    } catch (error: any) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: error.message,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const Validate = () => {
    const newErrors: { [key: string]: string | null } = {};

    if (!file) newErrors.file = 'Please upload a file';
    if (!application.firstname.trim())
      newErrors.firstname = 'First name is required';
    if (!application.lastname.trim())
      newErrors.lastname = 'Last name is required';

    if (!application.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(application.email)) {
      newErrors.email = 'Invalid email format';
    }

    const phoneNumberDigits = application.phoneNumber.replace(/\D/g, '');

    if (!application.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (phoneNumberDigits.length < 7) {
      newErrors.phoneNumber = 'Phone number must have at least 7 digits';
    }

    if (!isValidURL(application.websiteURL)) {
      newErrors.websiteURL = 'URL must start with http:// or https://';
    }

    if (Number(application.expectedSalary) < 0) {
      newErrors.expectedSalary = 'Salary must be 0 or greater';
    }

    setInputErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const countrySelector = (
    <Select
      value={selectedCountry?.value}
      onChange={handleCountryChange}
      disabled={spinner}
      placeholder="Country"
      className="w-16 lg:w-28"
      options={countryOptions}
    />
  );
  const clearExtractedData = () => {
    setFile(null);
    setCountryError(null);
    setApplication(new JobApplication());
  };
  const closeModal = () => {
    if (!isSubmitting) {
      onClose();
    }
  };
  const jobTitle = job?.name || job?.title || 'this opportunity';
  const companyName =
    job?.company || job?.client?.name || job?.owner?.companyName || '';

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="application-modal"
      overlayClassName="application-modal__overlay"
      contentLabel={`Apply for ${jobTitle}`}
      shouldCloseOnOverlayClick={!isSubmitting}
      shouldCloseOnEsc={!isSubmitting}
      ariaHideApp={false}
    >
      <div className="application-modal__shell">
        {isSubmitted ? (
          <div className="application-success" role="status">
            <button
              type="button"
              className="application-modal__close application-success__close"
              onClick={closeModal}
              aria-label="Close application confirmation"
            >
              <CloseOutlined aria-hidden />
            </button>
            <span className="application-success__icon">
              <CheckCircleFilled aria-hidden />
            </span>
            <span className="application-modal__eyebrow">
              Application submitted
            </span>
            <h2>You’re all set!</h2>
            <p className="application-success__lead">
              Your application for <strong>{jobTitle}</strong>
              {companyName ? (
                <>
                  {' '}
                  at <strong>{companyName}</strong>
                </>
              ) : null}{' '}
              was sent successfully.
            </p>

            <div className="application-success__summary">
              <div>
                <MailOutlined aria-hidden />
                <span>
                  <strong>Confirmation sent</strong>
                  We’ll send application updates to {application.email}.
                </span>
              </div>
              <div>
                <UserOutlined aria-hidden />
                <span>
                  <strong>Recruiter review</strong>
                  The hiring team will review your experience and contact you if
                  there’s a match.
                </span>
              </div>
            </div>

            <p className="application-success__note">
              <SafetyCertificateOutlined aria-hidden />
              No further action is needed right now.
            </p>
            <button
              type="button"
              className="public-button-primary application-success__action"
              onClick={closeModal}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <header className="application-modal__header">
              <div>
                <span className="application-modal__eyebrow">Easy Apply</span>
                <h2>Apply for {jobTitle}</h2>
                {companyName && <p>{companyName}</p>}
              </div>
              <button
                type="button"
                className="application-modal__close"
                onClick={closeModal}
                disabled={isSubmitting}
                aria-label="Close application form"
              >
                <CloseOutlined aria-hidden />
              </button>
            </header>

            <div
              className="application-modal__progress"
              aria-label="Application steps"
            >
              <span className={file ? 'is-complete' : 'is-current'}>
                {file ? <CheckCircleFilled /> : '1'}
                Resume
              </span>
              <i aria-hidden />
              <span className={file ? 'is-current' : ''}>2 Details</span>
              <i aria-hidden />
              <span>3 Submit</span>
            </div>

            <div className="application-modal__body">
              <section className="application-form-section">
                <div className="application-form-section__heading">
                  <span className="application-form-section__icon">
                    <InboxOutlined aria-hidden />
                  </span>
                  <div>
                    <h3>Resume</h3>
                    <p>Upload once and we’ll prefill your contact details.</p>
                  </div>
                  <span className="application-form-section__required">
                    Required
                  </span>
                </div>
                <div className="application-resume-upload">
                  <Dragger
                    {...props}
                    style={{
                      borderColor: inputErrors.file ? 'red' : undefined,
                      width: '100%',
                      height: 'auto',
                    }}
                    className="public-upload-zone application-resume-upload__dragger"
                    showUploadList={false}
                  >
                    {spinner ? (
                      <div
                        className="application-resume-upload__state"
                        role="status"
                      >
                        <Spin />
                        <strong>Reading your resume</strong>
                        <p className="ant-upload-hint">
                          We’re extracting your details. This usually takes a
                          few seconds.
                        </p>
                      </div>
                    ) : file ? (
                      <div className="application-resume-upload__success">
                        <span>
                          <CheckCircleFilled aria-hidden />
                        </span>
                        <div>
                          <strong>{file.name}</strong>
                          <p>Resume uploaded and details extracted.</p>
                        </div>
                        <AntButton
                          danger
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearExtractedData();
                          }}
                        >
                          Remove
                        </AntButton>
                      </div>
                    ) : (
                      <div className="application-resume-upload__empty">
                        <span>
                          <InboxOutlined aria-hidden />
                        </span>
                        <strong>Drop your resume here</strong>
                        <p>or click to browse · PDF or DOCX</p>
                      </div>
                    )}
                  </Dragger>

                  {inputErrors.file && (
                    <span className="jo-field-error application-field-error">
                      {inputErrors.file}
                    </span>
                  )}
                </div>
              </section>

              {countryError && (
                <div className="application-country-warning" role="alert">
                  <SafetyCertificateOutlined aria-hidden />
                  <span>{countryError}</span>
                </div>
              )}

              <section className="application-form-section">
                <div className="application-form-section__heading">
                  <span className="application-form-section__icon">
                    <UserOutlined aria-hidden />
                  </span>
                  <div>
                    <h3>Contact details</h3>
                    <p>Review the information extracted from your resume.</p>
                  </div>
                </div>

                <div className="application-form-grid">
                  {spinner ? (
                    <>
                      <Skeleton.Input style={{ width: '100%' }} active />
                      <Skeleton.Input style={{ width: '100%' }} active />
                    </>
                  ) : (
                    <>
                      <div className="application-field">
                        <label htmlFor="application-firstname">
                          First name
                        </label>
                        <Input
                          id="application-firstname"
                          type="string"
                          name="firstname"
                          placeholder="Enter your first name"
                          value={application.firstname}
                          onChange={OnChangeInput}
                          status={inputErrors.firstname ? 'error' : ''}
                        />
                        {inputErrors.firstname && (
                          <span className="jo-field-error application-field-error">
                            {inputErrors.firstname}
                          </span>
                        )}
                      </div>
                      <div className="application-field">
                        <label htmlFor="application-lastname">Last name</label>
                        <Input
                          id="application-lastname"
                          type="string"
                          name="lastname"
                          placeholder="Enter your last name"
                          value={application.lastname}
                          onChange={OnChangeInput}
                          status={inputErrors.lastname ? 'error' : ''}
                        />
                        {inputErrors.lastname && (
                          <span className="jo-field-error application-field-error">
                            {inputErrors.lastname}
                          </span>
                        )}
                      </div>
                    </>
                  )}

                  {spinner ? (
                    <>
                      <Skeleton.Input style={{ width: '100%' }} active />
                      <Skeleton.Input style={{ width: '100%' }} active />
                    </>
                  ) : (
                    <>
                      <div className="application-field">
                        <label htmlFor="application-phone">Phone number</label>
                        <Input
                          id="application-phone"
                          addonBefore={countrySelector}
                          className="no-spin"
                          type="string"
                          name="phoneNumber"
                          placeholder="Your phone number"
                          value={application.phoneNumber}
                          onChange={OnChangeInput}
                          status={inputErrors.phoneNumber ? 'error' : ''}
                        />
                        {inputErrors.phoneNumber && (
                          <span className="jo-field-error application-field-error">
                            {inputErrors.phoneNumber}
                          </span>
                        )}
                      </div>
                      <div className="application-field">
                        <label htmlFor="application-email">Email address</label>
                        <Input
                          id="application-email"
                          type="string"
                          name="email"
                          placeholder="you@example.com"
                          value={application.email}
                          onChange={OnChangeInput}
                          status={inputErrors.email ? 'error' : ''}
                        />
                        {inputErrors.email && (
                          <span className="jo-field-error application-field-error">
                            {inputErrors.email}
                          </span>
                        )}
                      </div>
                    </>
                  )}

                  <div className="application-field">
                    <label htmlFor="application-salary">
                      <DollarOutlined aria-hidden /> Expected salary
                      <em>Optional</em>
                    </label>
                    <div className="application-salary-input">
                      <Input
                        disabled
                        title="Currency"
                        name="currency"
                        value={selectedCurrency.label}
                        style={{
                          width: 40,
                          borderRight: 'none',
                          maxWidth: '110px',
                          pointerEvents: 'none',
                          textAlign: 'center',
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                        }}
                      />
                      <Input
                        id="application-salary"
                        className="no-spin"
                        title="Salary Expectation"
                        type="number"
                        name="expectedSalary"
                        min={0}
                        placeholder="Expected salary"
                        onChange={OnChangeNumberInput}
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                        }}
                      />
                    </div>
                    {inputErrors.expectedSalary && (
                      <span className="jo-field-error application-field-error">
                        {inputErrors.expectedSalary}
                      </span>
                    )}
                  </div>

                  {spinner ? (
                    <Skeleton.Input style={{ width: '100%' }} active />
                  ) : (
                    <div className="application-field">
                      <label htmlFor="application-profile">
                        <LinkOutlined aria-hidden /> Professional profile
                        <em>Optional</em>
                      </label>
                      <Input
                        id="application-profile"
                        type="string"
                        name="websiteURL"
                        placeholder="https://linkedin.com/in/your-profile"
                        value={application.websiteURL}
                        onChange={OnChangeInput}
                        status={inputErrors.websiteURL ? 'error' : ''}
                      />
                      {inputErrors.websiteURL && (
                        <span className="jo-field-error application-field-error">
                          {inputErrors.websiteURL}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className="application-form-section">
                <div className="application-form-section__heading">
                  <span className="application-form-section__icon">
                    <MailOutlined aria-hidden />
                  </span>
                  <div>
                    <h3>Application updates</h3>
                    <p>Choose how you’d like the recruiter to contact you.</p>
                  </div>
                  <span className="application-form-section__optional">
                    Optional
                  </span>
                </div>
                <div className="application-notification-options">
                  <CheckBox
                    title="Notify me by"
                    options={options}
                    selected={selecteds}
                    onChange={OnChangeSelected}
                    layout="horizontal"
                  />
                </div>
              </section>
            </div>

            <footer className="application-modal__footer">
              <div>
                <SafetyCertificateOutlined aria-hidden />
                <span>
                  Your information is shared only with the hiring team.
                </span>
              </div>
              <div className="application-modal__actions">
                <button
                  type="button"
                  className="public-button-secondary"
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="public-button-primary"
                  disabled={!!countryError || isSubmitting || spinner}
                  onClick={async () => {
                    if (!isSubmitting && Validate()) {
                      await Save();
                    }
                  }}
                >
                  {isSubmitting ? (
                    <Spin size="small" />
                  ) : (
                    <SendOutlined aria-hidden />
                  )}
                  {isSubmitting ? 'Submitting…' : 'Submit application'}
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </ReactModal>
  );
};

export default JobApplicationModal;
